import React, { useEffect, useState } from 'react';
import { Typography, Fab, Grid, Container, Box, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import useRestaurantStore from '../store/restaurantStore';
import RestaurantCard from '../components/restaurants/RestaurantCard';
import RestaurantEditDialog from '../components/restaurants/RestaurantEditDialog';

const RestaurantPage = () => {
  const { restaurants, loading, error, fetchRestaurants, updateRestaurant, createRestaurant, deleteRestaurant } = useRestaurantStore();
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [restaurantToDelete, setRestaurantToDelete] = useState(null);

  const handleRestaurantClick = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setDialogOpen(true);
  };

  const handleRestaurantDelete = (restaurant) => {
    setRestaurantToDelete(restaurant);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteRestaurant(restaurantToDelete.id);
      setDeleteDialogOpen(false);
      setRestaurantToDelete(null);
    } catch (error) {
      console.error('Failed to delete restaurant:', error);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedRestaurant(null);
  };

  const handleAddNewRestaurant = () => {
    setSelectedRestaurant(null);
    setDialogOpen(true);
  };

  const handleRestaurantSave = async (formData) => {
    try {
      // Structure the payload with required fields
      const restaurantPayload = {
        name: formData.name,
        address: formData.address,
        phone: formData.phone,
        cuisine_type: formData.cuisine_type,
        rating: formData.rating ? parseFloat(formData.rating) : null,
        notes: formData.notes
      };

      if (selectedRestaurant) {
        await updateRestaurant(selectedRestaurant.id, restaurantPayload);
      } else {
        await createRestaurant(restaurantPayload);
      }
      handleDialogClose();
    } catch (error) {
      console.error('Failed to save restaurant:', error);
    }
  };

  useEffect(() => {
    fetchRestaurants();
  }, []);

  if (loading) return <div>Loading restaurants...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        
        <Grid container spacing={2}>
          {restaurants.map((restaurant) => (
            <Grid item xs={12} sm={6} md={4} key={restaurant.id}>
              <RestaurantCard 
                restaurant={restaurant} 
                onRestaurantClick={handleRestaurantClick}
                onRestaurantDelete={handleRestaurantDelete}
              />
            </Grid>
          ))}
        </Grid>

        {restaurants.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No restaurants found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Click the + button to add your first restaurant
            </Typography>
          </Box>
        )}
      </Box>

      <Fab
        color="primary"
        aria-label="add"
        onClick={handleAddNewRestaurant}
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
      >
        <AddIcon />
      </Fab>

      <RestaurantEditDialog
        open={dialogOpen}
        restaurant={selectedRestaurant}
        onClose={handleDialogClose}
        onSave={handleRestaurantSave}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Restaurant
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete "{restaurantToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RestaurantPage;